import { useState, useEffect } from 'react';
import { CallLog, TextLog } from '@/types/TCRMLogs';
import { AgentNote, Customer } from '@/types/TCRMLead';
import { getCRMCallLogs, getCRMTextLogs, getLead, addLeadNote } from '@/api/crm/smartCRM';

interface UseCRMCallLogsReturn {
	callLogs: CallLog[];
	loading: boolean;
	error: string | null;
	hasMore: boolean;
	fetchCallLogs: (reset?: boolean) => void;
	loadMore: () => void;
}

interface UseCRMTextLogsReturn {
	textLogs: TextLog[];
	loading: boolean;
	error: string | null;
	hasMore: boolean;
	fetchTextLogs: (reset?: boolean) => void;
	loadMore: () => void;
}

interface UseCRMLeadReturn {
	lead: Customer | null;
	notes: AgentNote[];
	loading: boolean;
	error: string | null;
	fetchLead: () => void;
	addNote: (noteData: any) => Promise<void>;
}

export const useCRMCallLogs = (
	clientID: string,
	phoneNumber: string,
	businessPhoneNumber: string,
	isNoviUser: boolean = false,
): UseCRMCallLogsReturn => {
	const [callLogs, setCallLogs] = useState<CallLog[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastKey, setLastKey] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);

	const fetchCallLogs = async (reset: boolean = false) => {
		if (!clientID || !phoneNumber || !businessPhoneNumber) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await getCRMCallLogs(
				{
					clientID,
					leadPhoneNumber: phoneNumber,
					businessPhoneNumber,
					limit: 5,
					lastKey: reset ? undefined : lastKey || undefined,
				},
				isNoviUser,
			);

			if (response.success) {
				const newLogs = response.data?.logs || [];

				if (reset) {
					const sortedLogs = newLogs.sort((a, b) => {
						const dateA = new Date(a.startedTimestamp || a.date);
						const dateB = new Date(b.startedTimestamp || b.date);
						return dateB.getTime() - dateA.getTime();
					});
					setCallLogs(sortedLogs);
				} else {
					setCallLogs((prev) => {
						const combined = [...prev, ...newLogs];
						return combined.sort((a, b) => {
							const dateA = new Date(a.startedTimestamp || a.date);
							const dateB = new Date(b.startedTimestamp || b.date);
							return dateB.getTime() - dateA.getTime();
						});
					});
				}

				setLastKey(response.data?.lastKey || null);
				setHasMore(response.data?.hasMore || false);
			} else {
				setError('Failed to fetch call logs');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch call logs');
		} finally {
			setLoading(false);
		}
	};

	const loadMore = () => {
		if (!loading && hasMore && businessPhoneNumber) {
			fetchCallLogs(false);
		}
	};

	useEffect(() => {
		if (clientID && phoneNumber && businessPhoneNumber) {
			setCallLogs([]);
			setLastKey(null);
			setHasMore(true);
			fetchCallLogs(true);
		}
	}, [clientID, phoneNumber, businessPhoneNumber]);

	return {
		callLogs,
		loading,
		error,
		hasMore,
		fetchCallLogs,
		loadMore,
	};
};

export const useCRMTextLogs = (
	clientID: string,
	phoneNumber: string,
	isNoviUser: boolean = false,
): UseCRMTextLogsReturn => {
	const [textLogs, setTextLogs] = useState<TextLog[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastKey, setLastKey] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);

	const fetchTextLogs = async (reset: boolean = false) => {
		if (!clientID || !phoneNumber) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await getCRMTextLogs(
				{
					clientID,
					leadPhoneNumber: phoneNumber,
					limit: 50,
					lastKey: reset ? undefined : lastKey || undefined,
				},
				isNoviUser,
			);

			if (response.success) {
				const newLogs = response.data?.logs || [];

				if (reset) {
					const sortedLogs = newLogs.sort((a, b) => {
						const dateA = new Date(a.startedTimestamp || a.date);
						const dateB = new Date(b.startedTimestamp || b.date);
						return dateB.getTime() - dateA.getTime();
					});
					setTextLogs(sortedLogs);
				} else {
					setTextLogs((prev) => {
						const combined = [...prev, ...newLogs];
						return combined.sort((a, b) => {
							const dateA = new Date(a.startedTimestamp || a.date);
							const dateB = new Date(b.startedTimestamp || b.date);
							return dateB.getTime() - dateA.getTime();
						});
					});
				}

				setLastKey(response.data?.lastKey || null);
				setHasMore(response.data?.hasMore || false);
			} else {
				setError('Failed to fetch text logs');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch text logs');
		} finally {
			setLoading(false);
		}
	};

	const loadMore = () => {
		if (!loading && hasMore) {
			fetchTextLogs(false);
		}
	};

	useEffect(() => {
		if (clientID && phoneNumber) {
			setTextLogs([]);
			setLastKey(null);
			setHasMore(true);
			fetchTextLogs(true);
		}
	}, [clientID, phoneNumber]);

	return {
		textLogs,
		loading,
		error,
		hasMore,
		fetchTextLogs,
		loadMore,
	};
};

export const useCRMLead = (leadId: string, agentId: string): UseCRMLeadReturn => {
	const [lead, setLead] = useState<Customer | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchLead = async () => {
		if (!leadId || !agentId) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await getLead(leadId, agentId);

			if (response.success) {
				const leadData = response.data?.lead;
				setLead(leadData);
			} else {
				setError('Failed to fetch lead data');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to fetch lead data');
		} finally {
			setLoading(false);
		}
	};

	const addNote = async (noteData: any) => {
		setLoading(true);
		setError(null);

		try {
			const response = await addLeadNote(leadId, agentId, noteData);

			if (response.success) {
				await fetchLead();
				return Promise.resolve();
			} else {
				throw new Error('Failed to add note');
			}
		} catch (err: any) {
			setError(err.message || 'Failed to add note');
			throw err;
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (leadId && agentId) {
			fetchLead();
		}
	}, [leadId, agentId]);

	const notes = lead?.notes || [];
	const sortedNotes = notes.sort((a: AgentNote, b: AgentNote) => {
		const dateA = new Date(a.date);
		const dateB = new Date(b.date);
		return dateB.getTime() - dateA.getTime();
	});

	return {
		lead,
		notes: sortedNotes,
		loading,
		error,
		fetchLead,
		addNote,
	};
};
